import * as Sentry from "@sentry/react";
import "./i18n/config";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import App from "./App.tsx";
import { store } from "./app/store.ts";
import Login from "./features/login/Login.tsx";
import PaymentFailure from "./features/purchase/PaymentFailure.tsx";
import PaymentSuccess from "./features/purchase/PaymentSuccess.tsx";
import Purchase from "./features/purchase/Purchase.tsx";
import UserDetails from "./features/userDetails/UserDetails.tsx";
import HomeRoot from "./layout/HomeRoot/HomeRoot.tsx";
import TextDoc from "@components/TextDoc.tsx";
import ContactInfo from "@features/contactInfo/ContactInfo.tsx";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";

import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig, scopes } from "./authConfig.tsx";
import { ErrorPage } from "./layout/ErrorPage/ErrorPage.tsx";
import addSentry from "@utils/addSentry.ts";
import ChangePaymentCardSuccess from "@features/userDetails/ChangePaymentCardSuccess.tsx";
import ChangePaymentCardCancel from "@features/userDetails/ChangePaymentCardCancel.tsx";
import TokenHandler from "@features/emailAuthentication/TokenHandler.tsx";
import RequestSignInLink from "@features/emailAuthentication/RequestSignInLink.tsx";
import RequireEmailToken from "@features/emailAuthentication/RequireEmailToken.tsx";
import PaymentPolicy from "@features/paymentPolicy/PaymentPolicy.tsx";

if (!import.meta.env.DEV && import.meta.env.VITE_SENTRY_DSN) {
  addSentry();
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = (msalInstance: IPublicClientApplication) =>
  sentryCreateBrowserRouter([
    {
      element: <App pca={msalInstance} />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: "/",
          element: <HomeRoot />,
          children: [
            {
              path: "",
              element: <Login />,
            },
            {
              path: "/login",
              element: <Login />,
            },
          ],
        },
        {
          path: "/purchase",
          element: (
            <MsalAuthenticationTemplate
              interactionType={InteractionType.Redirect}
              authenticationRequest={{ scopes: scopes }}
            >
              <Purchase />
            </MsalAuthenticationTemplate>
          ),
        },
        {
          path: "/user",
          element: (
            <MsalAuthenticationTemplate
              interactionType={InteractionType.Redirect}
              authenticationRequest={{ scopes: scopes }}
            >
              <UserDetails />
            </MsalAuthenticationTemplate>
          ),
        },
        {
          path: "/success",
          element: <PaymentSuccess />,
        },
        {
          path: "/cancel",
          element: <PaymentFailure />,
        },
        {
          path: "/change-payment-card/success",
          element: <ChangePaymentCardSuccess />,
        },
        {
          path: "/change-payment-card/cancel",
          element: <ChangePaymentCardCancel />,
        },
        {
          path: "/contact-info",
          element: <ContactInfo />,
        },
        {
          path: "/privacy-policy",
          element: (
            <TextDoc
              titleKey="privacyPolicy.title"
              paragraphsKey="privacyPolicy.paragraphs"
            />
          ),
        },
        {
          path: "/payment-policy",
          element: <PaymentPolicy />,
        },
        {
          path: "/invitation",
          children: [
            {
              path: "authenticate",
              element: <TokenHandler />,
            },
            {
              path: "login",
              element: <RequestSignInLink />,
            },
            {
              path: "user",
              element: (
                <RequireEmailToken>
                  <UserDetails />
                </RequireEmailToken>
              ),
            },
            {
              path: "purchase",
              element: (
                <RequireEmailToken>
                  <Purchase />
                </RequireEmailToken>
              ),
            },
          ],
        },
        {
          path: "/*",
          element: <Navigate to="/login" replace />,
        },
      ],
    },
  ]);

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance
  .initialize()
  .then(() => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <Provider store={store}>
            <RouterProvider router={router(msalInstance)} />
          </Provider>
        </MsalProvider>
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    console.error(error);
  });
